import { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function OfflineIndicator() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncPending, setSyncPending] = useState(false);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
      setIsOffline(false);
      // Trigger background sync
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'TRIGGER_SYNC' });
        setSyncPending(true);
      }
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);

    // Listen for sync complete messages from SW
    const handleMessage = (event) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        setSyncPending(false);
        if (event.data.count > 0) {
          toast.success(`Synced ${event.data.count} offline action(s)`);
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  if (!isOffline && !syncPending) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 py-1.5 px-4 text-center text-xs font-semibold ${
      isOffline ? 'bg-red-500 text-white' : 'bg-yellow-500 text-yellow-900'
    }`} data-testid="offline-indicator">
      {isOffline ? (
        <span className="flex items-center justify-center gap-1.5">
          <WifiOff className="w-3.5 h-3.5" />
          You're offline — changes will sync when back online
        </span>
      ) : (
        <span className="flex items-center justify-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          Syncing offline changes...
        </span>
      )}
    </div>
  );
}
